import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { ChatMessage, ChatMilestone } from '../../data/chatData';

interface ChatViewerProps {
  messages: ChatMessage[];
  milestones: ChatMilestone[];
  activeMilestone: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onGoToAnalysis: () => void;
}

const BATCH_SIZE = 140;

export const ChatViewer: React.FC<ChatViewerProps> = ({
  messages,
  milestones,
  activeMilestone,
  searchQuery,
  onSearchChange,
  onGoToAnalysis,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const [displayCount, setDisplayCount] = useState<number>(BATCH_SIZE);
  const [isPlayingVoice, setIsPlayingVoice] = useState<string | null>(null);

  const isPrependingRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);

  // Maintain precise scroll position when older messages are prepended
  useLayoutEffect(() => {
    if (isPrependingRef.current && containerRef.current) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const heightDiff = newScrollHeight - prevScrollHeightRef.current;
      containerRef.current.scrollTop = prevScrollTopRef.current + heightDiff;
      isPrependingRef.current = false;
    }
  }, [displayCount]);

  // Filter messages based on search query or active milestone
  const filteredMessages = useMemo(() => {
    let result = messages;

    if (activeMilestone !== 'all') {
      const ms = milestones.find(m => m.id === activeMilestone);
      if (ms) {
        result = result.filter(
          msg => msg.date >= ms.startDate && msg.date <= ms.endDate
        );
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        msg =>
          msg.text.toLowerCase().includes(q) ||
          (msg.mediaName && msg.mediaName.toLowerCase().includes(q)) ||
          msg.dateLabel.toLowerCase().includes(q)
      );
    }

    return result;
  }, [messages, activeMilestone, milestones, searchQuery]);

  // Visible window of messages
  const visibleMessages = useMemo(() => {
    if (filteredMessages.length <= displayCount) {
      return filteredMessages;
    }
    return filteredMessages.slice(filteredMessages.length - displayCount);
  }, [filteredMessages, displayCount]);

  const hasEarlierMessages = filteredMessages.length > displayCount;

  // Auto-scroll to bottom only on milestone switch or search query change
  useEffect(() => {
    setDisplayCount(BATCH_SIZE);
    const timer = setTimeout(() => {
      bottomAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 60);
    return () => clearTimeout(timer);
  }, [activeMilestone, searchQuery]);

  const handleLoadEarlier = () => {
    if (containerRef.current) {
      prevScrollHeightRef.current = containerRef.current.scrollHeight;
      prevScrollTopRef.current = containerRef.current.scrollTop;
      isPrependingRef.current = true;

      setDisplayCount(prev => Math.min(prev + BATCH_SIZE, filteredMessages.length));
    }
  };

  const scrollToBottom = () => {
    bottomAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleVoicePlayback = (id: string) => {
    setIsPlayingVoice(prev => (prev === id ? null : id));
  };

  return (
    <div className="chat-viewport">
      {/* Scrollable Messages Stream */}
      <div className="chat-messages-container" ref={containerRef} role="log" aria-label="Conversation log">
        {hasEarlierMessages && (
          <div className="load-earlier-wrapper">
            <button
              type="button"
              className="load-earlier-btn"
              onClick={handleLoadEarlier}
            >
              Load earlier moments ({filteredMessages.length - displayCount} earlier)
            </button>
          </div>
        )}

        {visibleMessages.length === 0 ? (
          <div className="chat-empty-state">
            <div className="empty-icon">💭</div>
            <h3 className="empty-title">Quiet in this period</h3>
            <p className="empty-desc">
              {searchQuery
                ? `No messages matching "${searchQuery}"`
                : 'No recorded interactions in this selected timeframe.'}
            </p>
          </div>
        ) : (
          visibleMessages.map((msg, index) => {
            const isOutgoing = msg.sender === 'Ratnadwip';
            const prevMsg = visibleMessages[index - 1];
            const nextMsg = visibleMessages[index + 1];

            const isNewDay = !prevMsg || prevMsg.date !== msg.date;
            const isSameSenderAsPrev = !isNewDay && prevMsg && prevMsg.sender === msg.sender;
            const isSameSenderAsNext = nextMsg && nextMsg.date === msg.date && nextMsg.sender === msg.sender;

            return (
              <React.Fragment key={msg.id}>
                {/* Natural Date Divider */}
                {isNewDay && (
                  <div className="chat-date-separator">
                    <span className="chat-date-pill">{msg.dateLabel}</span>
                  </div>
                )}

                {/* Milestone Marker */}
                {msg.milestone && (!prevMsg || prevMsg.milestone !== msg.milestone) && (
                  <div className="chat-milestone-marker">
                    <span className="chat-milestone-badge">
                      <span>✦</span> {msg.milestone}
                    </span>
                  </div>
                )}

                {/* Message Bubble Row with Natural Grouping */}
                <div
                  className={`chat-message-row ${isOutgoing ? 'outgoing' : 'incoming'} ${
                    isSameSenderAsPrev ? 'grouped-followup' : 'grouped-start'
                  } ${isSameSenderAsNext ? 'has-next' : 'is-tail'}`}
                >
                  <div className="chat-bubble">
                    {/* Only show sender name at start of cluster for incoming */}
                    {!isOutgoing && !isSameSenderAsPrev && (
                      <div className="bubble-sender">Ankita</div>
                    )}

                    {/* Media Attachments */}
                    {msg.type === 'voice_note' && (
                      <div className="voice-card">
                        <button
                          type="button"
                          className="voice-play-btn"
                          onClick={() => toggleVoicePlayback(msg.id)}
                          aria-label="Play audio note"
                        >
                          {isPlayingVoice === msg.id ? '⏸' : '▶'}
                        </button>
                        <div className="voice-wave">
                          <span className="wave-bar" style={{ height: '7px' }} />
                          <span className="wave-bar" style={{ height: '14px' }} />
                          <span className="wave-bar" style={{ height: '18px' }} />
                          <span className="wave-bar" style={{ height: '10px' }} />
                          <span className="wave-bar" style={{ height: '16px' }} />
                          <span className="wave-bar" style={{ height: '8px' }} />
                          <span className="wave-bar" style={{ height: '13px' }} />
                          <span className="wave-bar" style={{ height: '6px' }} />
                        </div>
                        <span className="media-subtitle">
                          {isPlayingVoice === msg.id ? 'Playing...' : 'Voice Note'}
                        </span>
                      </div>
                    )}

                    {msg.type === 'audio' && (
                      <div className="media-card">
                        <span className="media-icon">🎵</span>
                        <div className="media-details">
                          <span className="media-title">{msg.mediaName || 'Audio track'}</span>
                          <span className="media-subtitle">Music</span>
                        </div>
                      </div>
                    )}

                    {msg.type === 'image' && (
                      <div className="media-card">
                        <span className="media-icon">📷</span>
                        <div className="media-details">
                          <span className="media-title">{msg.mediaName || 'Photo'}</span>
                          <span className="media-subtitle">Shared Photo</span>
                        </div>
                      </div>
                    )}

                    {msg.type === 'video' && (
                      <div className="media-card">
                        <span className="media-icon">🎬</span>
                        <div className="media-details">
                          <span className="media-title">{msg.mediaName || 'Video'}</span>
                          <span className="media-subtitle">Shared Video</span>
                        </div>
                      </div>
                    )}

                    {msg.type === 'document' && (
                      <div className="media-card">
                        <span className="media-icon">📄</span>
                        <div className="media-details">
                          <span className="media-title">{msg.mediaName || 'Document.pdf'}</span>
                          <span className="media-subtitle">File Attachment</span>
                        </div>
                      </div>
                    )}

                    {msg.type === 'contact' && (
                      <div className="media-card">
                        <span className="media-icon">👤</span>
                        <div className="media-details">
                          <span className="media-title">{msg.mediaName || 'Contact Card'}</span>
                          <span className="media-subtitle">Contact</span>
                        </div>
                      </div>
                    )}

                    {msg.type === 'link' && msg.linkUrl && (
                      <a
                        href={msg.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-card"
                      >
                        <span>🔗</span>
                        <span className="link-text">{msg.linkUrl}</span>
                      </a>
                    )}

                    {/* Text Content */}
                    {msg.text && <div className="bubble-text">{msg.text}</div>}

                    {/* Meta Time & Status */}
                    <div className="bubble-meta">
                      {msg.platform === 'instagram' && <span className="platform-pill">IG</span>}
                      <span>{msg.time}</span>
                      {isOutgoing && <span className="bubble-ticks">✓✓</span>}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}

        <div ref={bottomAnchorRef} />
      </div>

      {/* Floating Scroll Controls */}
      <div className="chat-floating-controls" aria-label="Scroll controls">
        <button
          type="button"
          className="scroll-fab-btn"
          onClick={scrollToTop}
          title="Scroll to beginning"
          aria-label="Scroll to top"
        >
          ▲
        </button>
        <button
          type="button"
          className="scroll-fab-btn"
          onClick={scrollToBottom}
          title="Scroll to latest"
          aria-label="Scroll to bottom"
        >
          ▼
        </button>
      </div>

      {/* Human, Clean Bottom Bar */}
      <div className="chat-input-bar">
        <div className="chat-input-wrapper">
          <input
            type="text"
            className="chat-input-field"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search through 33,000+ moments..."
            aria-label="Search conversation"
          />
          {searchQuery && (
            <button
              type="button"
              className="chat-search-close"
              onClick={() => onSearchChange('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
          <button
            type="button"
            className="clean-ai-chip-btn"
            onClick={onGoToAnalysis}
            title="Open AI Analysis & Insights"
          >
            ✦ Ask AI
          </button>
        </div>

        <button
          type="button"
          className="chat-send-btn"
          onClick={scrollToBottom}
          title="Jump to latest"
          aria-label="Scroll to newest messages"
        >
          ↓
        </button>
      </div>
    </div>
  );
};
